/**
 * Chains (2026-10-23, OPVisual's ask x4 item 2, `OPApps/opbuttons-opvisual.md`): an Action timeline whose steps run
 * IN ORDER - named actions, with a wait before any of them in ms, beats, bars or "the next bar" - instead of sitting
 * on a ruler. Operator (via OPVisual, 2026-10-05): "timelines was spawned from the idea of sequential timed actions,
 * but musical. sometimes the user might not need musical, and just a straight up, do this, wait 300ms, then do that".
 *
 * A chain is `Timeline { tier: 'action', form: 'chain' }` with ONE lane; its steps' array order is the run order (each
 * step's `position` is kept equal to its index so anything that sorts by position agrees). OPBridge plays it as its
 * `"tier": "chain"` (S307, `timeline_chains`): ms waits run with the transport stopped, musical waits hold until the
 * clock runs. This module is the pure part: the words, the limits and the edits; `bridgeSync.ts::chainWire` sends it.
 */
import type { CueStep, Timeline, TimelineLane, TimelineStep, WaitStep, WaitUnit } from '@opbuttons/schema';

/** OPBridge refuses a wait over an hour or 1024 bars (PROTOCOL "Chains"). */
export const CHAIN_MAX_MS = 3_600_000;
export const CHAIN_MAX_BARS = 1024;

export const isChain = (t: Pick<Timeline, 'tier' | 'form'> | undefined | null): boolean => !!t && t.tier === 'action' && t.form === 'chain';
export const isWaitStep = (s: TimelineStep): s is WaitStep => s.kind === 'wait';
/** A step that fires a named action (`{module: 'action', command: 'fire', args: {id}}`). */
export function firedActionId(s: TimelineStep): string | null {
  if (s.kind !== 'cue') return null;
  const a = (s as CueStep).action as { module?: string; command?: string; args?: { id?: unknown } } | undefined;
  return a?.module === 'action' && a.command === 'fire' && typeof a.args?.id === 'string' ? a.args.id : null;
}

/** "300 ms", "2 beats", "1 bar", "the next bar". */
export function waitWords(w: Pick<WaitStep, 'unit' | 'amount'>): string {
  if (w.unit === 'nextBar') return 'the next bar';
  const n = w.amount ?? 0;
  const num = Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000);
  if (w.unit === 'ms') return `${num} ms`;
  const word = w.unit === 'beats' ? 'beat' : 'bar';
  return `${num} ${word}${n === 1 ? '' : 's'}`;
}

/** Why a wait would be refused by OPBridge, or null when it is fine. */
export function waitProblem(w: Pick<WaitStep, 'unit' | 'amount'>): string | null {
  if (w.unit === 'nextBar') return null;
  const n = w.amount;
  if (n === undefined || !Number.isFinite(n) || n <= 0) return 'A wait needs a length above 0';
  if (w.unit === 'ms' && n > CHAIN_MAX_MS) return 'A wait can be at most an hour';
  if ((w.unit === 'bars' && n > CHAIN_MAX_BARS) || (w.unit === 'beats' && n > CHAIN_MAX_BARS * 4)) return 'A wait can be at most 1024 bars';
  return null;
}

/** The chain's one lane (made if missing), and its steps in run order. */
export function chainLane(t: Timeline): TimelineLane {
  return t.lanes[0] ?? { id: `${t.id}_chain`, name: 'Chain', laneKind: 'mixed', steps: [] };
}
export const chainSteps = (t: Timeline): TimelineStep[] => chainLane(t).steps;

/** The same timeline with its chain's steps replaced, positions renumbered to the run order. */
export function withChainSteps(t: Timeline, steps: TimelineStep[]): Timeline {
  const lane = chainLane(t);
  const numbered = steps.map((s, i) => (s.position === i ? s : { ...s, position: i }));
  return { ...t, lanes: [{ ...lane, steps: numbered }, ...t.lanes.slice(1)] };
}

/** Move one step from `from` to `to` (array indices), the rest keeping their order. */
export function moveChainStep(t: Timeline, from: number, to: number): Timeline {
  const steps = [...chainSteps(t)];
  if (from < 0 || from >= steps.length || to < 0 || to >= steps.length || from === to) return t;
  const [s] = steps.splice(from, 1);
  steps.splice(to, 0, s!);
  return withChainSteps(t, steps);
}

export function newWait(id: string, unit: WaitUnit, amount?: number): WaitStep {
  return unit === 'nextBar' ? { id, kind: 'wait', position: 0, unit } : { id, kind: 'wait', position: 0, unit, amount: amount ?? (unit === 'ms' ? 300 : 1) };
}
export function newFire(id: string, actionId: string, label: string): CueStep {
  return { id, kind: 'cue', position: 0, action: { module: 'action', command: 'fire', args: { id: actionId } }, label } as CueStep;
}
